// Small dense-matrix helpers for the sketch-wall constraint solver. The
// sketches this solves are tiny (tens of variables at most), so plain
// array-of-arrays Gaussian elimination is fast enough and easy to verify —
// no need for a real linear-algebra dependency.

function vecSub(a, b) {
  return a.map((v, i) => v - b[i]);
}

function vecAdd(a, b) {
  return a.map((v, i) => v + b[i]);
}

function vecScale(a, s) {
  return a.map(v => v * s);
}

function vecDot(a, b) {
  let sum = 0;
  for (let i = 0; i < a.length; i += 1) sum += a[i] * b[i];
  return sum;
}

function vecNorm(a) {
  return Math.sqrt(vecDot(a, a));
}

function matTranspose(matrix) {
  if (!matrix.length) return [];
  const rows = matrix.length;
  const cols = matrix[0].length;
  const result = [];
  for (let c = 0; c < cols; c += 1) {
    const row = new Array(rows);
    for (let r = 0; r < rows; r += 1) row[r] = matrix[r][c];
    result.push(row);
  }
  return result;
}

function matVecMul(matrix, vector) {
  return matrix.map(row => vecDot(row, vector));
}

function matMul(a, b) {
  const bt = matTranspose(b);
  return a.map(row => bt.map(col => vecDot(row, col)));
}

// Solves A x = b for square A via Gaussian elimination with partial
// pivoting. Returns null if A is (numerically) singular.
function solveLinearSystem(a, b, tol = 1e-9) {
  const n = a.length;
  const m = a.map((row, i) => [...row, b[i]]);
  for (let col = 0; col < n; col += 1) {
    let pivotRow = col;
    let pivotValue = Math.abs(m[col][col]);
    for (let r = col + 1; r < n; r += 1) {
      if (Math.abs(m[r][col]) > pivotValue) {
        pivotValue = Math.abs(m[r][col]);
        pivotRow = r;
      }
    }
    if (pivotValue < tol) return null;
    if (pivotRow !== col) [m[col], m[pivotRow]] = [m[pivotRow], m[col]];
    const pivot = m[col][col];
    for (let c = col; c <= n; c += 1) m[col][c] /= pivot;
    for (let r = 0; r < n; r += 1) {
      if (r === col) continue;
      const factor = m[r][col];
      if (factor === 0) continue;
      for (let c = col; c <= n; c += 1) m[r][c] -= factor * m[col][c];
    }
  }
  return m.map(row => row[n]);
}

// Row-reduces `matrix` (m x n) to RREF and returns { pivotCols, reduced }.
function rowReduce(matrix, tol = 1e-9) {
  const reduced = matrix.map(row => [...row]);
  const rows = reduced.length;
  const cols = rows ? reduced[0].length : 0;
  const pivotCols = [];
  let pivotRow = 0;
  for (let col = 0; col < cols && pivotRow < rows; col += 1) {
    let best = pivotRow;
    let bestValue = Math.abs(reduced[pivotRow][col]);
    for (let r = pivotRow + 1; r < rows; r += 1) {
      if (Math.abs(reduced[r][col]) > bestValue) {
        bestValue = Math.abs(reduced[r][col]);
        best = r;
      }
    }
    if (bestValue < tol) continue;
    if (best !== pivotRow) [reduced[pivotRow], reduced[best]] = [reduced[best], reduced[pivotRow]];
    const pivot = reduced[pivotRow][col];
    for (let c = col; c < cols; c += 1) reduced[pivotRow][c] /= pivot;
    for (let r = 0; r < rows; r += 1) {
      if (r === pivotRow) continue;
      const factor = reduced[r][col];
      if (factor === 0) continue;
      for (let c = col; c < cols; c += 1) reduced[r][c] -= factor * reduced[pivotRow][c];
    }
    pivotCols.push(col);
    pivotRow += 1;
  }
  return { pivotCols, reduced, rank: pivotRow };
}

// Nullspace basis of `matrix` (m x n), returned as an array of orthonormal
// n-length vectors (via RREF free-variable back-substitution + Gram-Schmidt).
// `numCols` must be passed explicitly to handle the m=0 (no constraints yet)
// case, where every variable is free and the nullspace is the full space.
function nullspaceBasis(matrix, numCols, tol = 1e-7) {
  const rows = matrix.length;
  const cols = rows ? matrix[0].length : Number(numCols) || 0;
  if (!cols) return { basis: [], rank: 0 };
  if (!rows) {
    const basis = [];
    for (let i = 0; i < cols; i += 1) {
      const v = new Array(cols).fill(0);
      v[i] = 1;
      basis.push(v);
    }
    return { basis, rank: 0 };
  }
  const { pivotCols, reduced, rank } = rowReduce(matrix, tol);
  const pivotSet = new Set(pivotCols);
  const freeCols = [];
  for (let c = 0; c < cols; c += 1) if (!pivotSet.has(c)) freeCols.push(c);

  const basis = freeCols.map(freeCol => {
    const v = new Array(cols).fill(0);
    v[freeCol] = 1;
    for (let p = 0; p < pivotCols.length; p += 1) {
      const col = pivotCols[p];
      v[col] = -reduced[p][freeCol];
    }
    return v;
  });

  // Gram-Schmidt orthonormalization so later projections are simple dot products.
  const ortho = [];
  for (const vector of basis) {
    let v = [...vector];
    for (const u of ortho) {
      const proj = vecDot(v, u);
      v = vecSub(v, vecScale(u, proj));
    }
    const norm = vecNorm(v);
    if (norm > tol) ortho.push(vecScale(v, 1 / norm));
  }
  return { basis: ortho, rank };
}

export {
  vecSub,
  vecAdd,
  vecScale,
  vecDot,
  vecNorm,
  matTranspose,
  matVecMul,
  matMul,
  solveLinearSystem,
  rowReduce,
  nullspaceBasis
};
